/**
 * Template Preview Upload Service
 *
 * Handles uploading template preview screenshots to Supabase Storage
 * and managing preview image lifecycle.
 *
 * Storage Path: Email-Template-Previews/User-Templates/{templateId}_preview_{timestamp}.webp
 */

import { supabase } from '@/integrations/supabase/client';

/**
 * Upload template preview to Supabase Storage
 *
 * Uploads the processed preview image to the dedicated template previews
 * folder in Supabase Storage. Uses timestamp-based naming to avoid conflicts
 * and enable cleanup of old previews.
 *
 * Storage Details:
 * - Bucket: 'images'
 * - Path: Email-Template-Previews/User-Templates/
 * - Filename Format: {templateId}_preview_{timestamp}.webp
 * - Public Access: Yes (templates are public content)
 *
 * @param templateId - Template ID (UUID)
 * @param imageBlob - Processed image blob (WebP format recommended)
 * @returns Promise<string> - Public URL of uploaded image
 * @throws Error if upload fails
 */
export const uploadTemplatePreview = async (
  templateId: string,
  imageBlob: Blob
): Promise<string> => {
  try {
    console.log('[uploadTemplatePreview] Starting upload for template:', templateId);
    console.log('[uploadTemplatePreview] Image size:', (imageBlob.size / 1024).toFixed(2), 'KB');

    // Generate unique filename with timestamp
    const timestamp = Date.now();
    const filename = `${templateId}_preview_${timestamp}.webp`;
    const path = `Email-Template-Previews/User-Templates/${filename}`;

    console.log('[uploadTemplatePreview] Upload path:', path);

    // Upload to Supabase Storage
    const { data, error } = await supabase.storage
      .from('images')
      .upload(path, imageBlob, {
        contentType: 'image/webp',
        cacheControl: '3600',  // Cache for 1 hour
        upsert: true  // Replace if exact filename exists (unlikely with timestamp)
      });

    if (error) {
      console.error('[uploadTemplatePreview] Upload error:', error);
      throw new Error(`Upload failed: ${error.message}`);
    }

    console.log('[uploadTemplatePreview] Upload successful:', data.path);

    // Get public URL for the uploaded file
    const { data: urlData } = supabase.storage
      .from('images')
      .getPublicUrl(path);

    const publicUrl = urlData.publicUrl;
    console.log('[uploadTemplatePreview] Public URL:', publicUrl);

    return publicUrl;

  } catch (error) {
    console.error('[uploadTemplatePreview] Exception:', error);
    throw new Error(
      `Failed to upload template preview: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
};

/**
 * Delete old template previews
 *
 * Cleans up previous preview images for a template to save storage space.
 * Keeps only the most recent preview and deletes older ones.
 *
 * This is a non-critical operation - failures are logged but not thrown.
 *
 * @param templateId - Template ID to clean up
 * @returns Promise<void>
 */
export const deleteOldTemplatePreviews = async (
  templateId: string
): Promise<void> => {
  try {
    console.log('[deleteOldTemplatePreviews] Checking for old previews:', templateId);

    // List all preview files for this template
    const { data: files, error: listError } = await supabase.storage
      .from('images')
      .list('Email-Template-Previews/User-Templates/', {
        search: `${templateId}_preview_`  // Find all files for this template
      });

    if (listError) {
      console.warn('[deleteOldTemplatePreviews] List error (non-critical):', listError);
      return;
    }

    if (!files || files.length === 0) {
      console.log('[deleteOldTemplatePreviews] No old previews found');
      return;
    }

    console.log('[deleteOldTemplatePreviews] Found', files.length, 'preview file(s)');

    // Sort files by timestamp (newest first)
    const sortedFiles = files.sort((a, b) => {
      // Extract timestamp from filename: {templateId}_preview_{timestamp}.webp
      const aTimestamp = parseInt(
        a.name.split('_preview_')[1]?.split('.')[0] || '0'
      );
      const bTimestamp = parseInt(
        b.name.split('_preview_')[1]?.split('.')[0] || '0'
      );
      return bTimestamp - aTimestamp;  // Newest first
    });

    // Keep the newest, delete the rest
    const filesToDelete = sortedFiles
      .slice(1)  // Skip first (newest)
      .map(f => `Email-Template-Previews/User-Templates/${f.name}`);

    if (filesToDelete.length === 0) {
      console.log('[deleteOldTemplatePreviews] Only one preview, nothing to delete');
      return;
    }

    console.log('[deleteOldTemplatePreviews] Deleting', filesToDelete.length, 'old preview(s)');

    const { error: deleteError } = await supabase.storage
      .from('images')
      .remove(filesToDelete);

    if (deleteError) {
      console.warn('[deleteOldTemplatePreviews] Delete failed (non-critical):', deleteError);
    } else {
      console.log('[deleteOldTemplatePreviews] Cleanup successful');
    }

  } catch (error) {
    // Non-critical operation - log but don't throw
    console.warn('[deleteOldTemplatePreviews] Exception (non-critical):', error);
  }
};

/**
 * Delete all previews for a template
 *
 * Used when a template is deleted to clean up all associated preview images.
 *
 * @param templateId - Template ID
 * @returns Promise<void>
 */
export const deleteAllTemplatePreviews = async (
  templateId: string
): Promise<void> => {
  try {
    console.log('[deleteAllTemplatePreviews] Deleting all previews for:', templateId);

    // List all preview files for this template
    const { data: files, error: listError } = await supabase.storage
      .from('images')
      .list('Email-Template-Previews/User-Templates/', {
        search: `${templateId}_preview_`
      });

    if (listError || !files || files.length === 0) {
      console.log('[deleteAllTemplatePreviews] No previews to delete');
      return;
    }

    // Delete all files
    const filesToDelete = files.map(
      f => `Email-Template-Previews/User-Templates/${f.name}`
    );

    console.log('[deleteAllTemplatePreviews] Deleting', filesToDelete.length, 'preview(s)');

    const { error: deleteError } = await supabase.storage
      .from('images')
      .remove(filesToDelete);

    if (deleteError) {
      console.warn('[deleteAllTemplatePreviews] Delete failed:', deleteError);
    } else {
      console.log('[deleteAllTemplatePreviews] All previews deleted');
    }

  } catch (error) {
    console.warn('[deleteAllTemplatePreviews] Exception:', error);
  }
};

/**
 * Get preview URL from database record
 *
 * Helper to extract preview URL from template object
 *
 * @param template - Template object with preview_image_url field
 * @returns string | null - Preview URL or null if not set
 */
export const getTemplatePreviewUrl = (
  template: { preview_image_url?: string | null }
): string | null => {
  return template.preview_image_url || null;
};

/**
 * Check if template has a preview
 *
 * @param template - Template object
 * @returns boolean - True if preview URL exists
 */
export const hasTemplatePreview = (
  template: { preview_image_url?: string | null }
): boolean => {
  return !!template.preview_image_url;
};
